import { motion } from "motion/react";
import { useState } from "react";
import { cn } from "@/lib/utils";

type ChevronButtonProps = {
	text: string;
	variant?: "default" | "inverted" | "secondary";
	className?: string;
	onClick?: () => void;
	as?: "button" | "div";
};

export default function ChevronButton({
	text,
	variant = "default",
	className,
	onClick,
	as: Component = "button",
}: ChevronButtonProps) {
	const [isHovered, setIsHovered] = useState(false);

	const isInverted = variant === "inverted";
	const isSecondary = variant === "secondary";

	// Variant Styles
	const baseStyles =
		"flex items-center gap-2 rounded py-1 shadow-xs text-base font-bold";
	const variantStyles = cn(
		isInverted &&
			"bg-orange text-white hover:bg-primary-foreground/90 pr-3 pl-2",

		!isInverted &&
			!isSecondary &&
			"bg-orange text-white hover:bg-primary pl-3 pr-2",

		isSecondary &&
			"bg-orange text-white hover:bg-secondary hover:text-secondary-foreground  pl-3 pr-2",
	);

	return (
		<Component
			type={Component === "button" ? "button" : undefined}
			className={cn(baseStyles, variantStyles, className)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={onClick}
		>
			{isInverted && (
				<motion.svg
					width="15"
					height="15"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<title>Chevron</title>
					<motion.path
						d="M5 12H19"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						initial={{ opacity: 0, x: 10 }}
						animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : 10 }}
						transition={{ duration: 0.2 }}
					/>
					<motion.path
						d="M12 5L5 12L12 19"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						initial={{ x: 0 }}
						animate={{ x: isHovered ? 0 : 7 }}
						transition={{ duration: 0.2 }}
					/>
				</motion.svg>
			)}

			<span>{text}</span>

			{!isInverted && (
				<motion.svg
					width="15"
					height="15"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<title>Chevron</title>
					<motion.path
						d="M5 12H19"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						initial={{ opacity: 0, x: -10 }}
						animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : -10 }}
						transition={{ duration: 0.2 }}
					/>
					<motion.path
						d="M12 5L19 12L12 19"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						initial={{ x: 0 }}
						animate={{ x: isHovered ? 0 : -7 }}
						transition={{ duration: 0.2 }}
					/>
				</motion.svg>
			)}
		</Component>
	);
}
